import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getMyPurchaseOrder } from '../api/vendorApi';
import { serverURL } from '../api/vendorAxios';
import { VpSpinner, money, fmtDate } from '../components/VpUI';
import VpIcon from '../components/VpIcon';

// Read-only detail of an approved purchase order for the vendor.
const VendorPurchaseOrderDetail = () => {
	const { id } = useParams();
	const [loading, setLoading] = useState(true);
	const [po, setPo] = useState<any>(null);
	const [error, setError] = useState('');

	useEffect(() => {
		(async () => {
			try {
				const data = await getMyPurchaseOrder(id as string);
				setPo(data);
			} catch (err: any) {
				setError(err?.apiMessage || 'Could not load the purchase order.');
			} finally {
				setLoading(false);
			}
		})();
	}, [id]);

	if (loading) return <VpSpinner />;
	if (error) return <div className='vp-alert vp-alert-error'>{error}</div>;

	const products = po?.products || [];
	const documents = po?.documents || [];
	const fileUrl = (p: string) => `${serverURL}/${String(p || '').replace(/^\/+/, '')}`;

	return (
		<div>
			{/* Approved banner */}
			<div className='vp-congrats'>
				<div className='vp-congrats-emoji'>
					<VpIcon name='accepted' size={40} />
				</div>
				<div>
					<h3>Your Purchase Order has been approved.</h3>
					<p>Review the order details below and proceed with fulfilment.</p>
				</div>
			</div>

			{/* Header */}
			<div className='vp-card vp-card-pad' style={{ marginBottom: 18 }}>
				<div className='vp-row-between'>
					<div>
						<div className='vp-muted' style={{ fontSize: 13 }}>
							Purchase Order
						</div>
						<div style={{ fontSize: 20, fontWeight: 700 }}>
							{po?.purchase_order_no}
						</div>
						<div className='vp-muted' style={{ fontSize: 13, marginTop: 4 }}>
							{po?.company?.name || '—'} · {fmtDate(po?.date || po?.createdAt)}
						</div>
					</div>
					<div style={{ textAlign: 'right' }}>
						<span className='vp-badge vp-badge-confirmed'>Approved</span>
						<div className='vp-muted' style={{ fontSize: 13, marginTop: 8 }}>
							Expected Delivery
						</div>
						<div style={{ fontWeight: 600 }}>
							{po?.expected_delivery_date
								? fmtDate(po.expected_delivery_date)
								: '—'}
						</div>
					</div>
				</div>
			</div>

			{/* Line items */}
			<div className='vp-card'>
				<div className='vp-card-head'>
					<h3>Order Items</h3>
				</div>
				<div className='vp-table-wrap'>
					<table className='vp-table'>
						<thead>
							<tr>
								<th>Product</th>
								<th>Usage / Used For</th>
								<th>Qty</th>
								<th>Rate</th>
								<th>Discount</th>
								<th>Tax %</th>
								<th style={{ textAlign: 'right' }}>Amount</th>
							</tr>
						</thead>
						<tbody>
							{products.map((p: any) => (
								<tr key={p._id}>
									<td style={{ fontWeight: 600 }}>
										{p.productInfo?.name || p.product_description || 'Item'}
									</td>
									<td>{p?.usage_note || '—'}</td>
									<td>{p.quantity}</td>
									<td>{money(p.rate)}</td>
									<td>
										{p.discount || 0}
										{p.discountType === 'fixed' ? ' ₹' : ' %'}
									</td>
									<td>{p.tax || 0}%</td>
									<td style={{ textAlign: 'right', fontWeight: 600 }}>
										{money(p.amount_with_tax)}
									</td>
								</tr>
							))}
						</tbody>
						<tfoot>
							<tr>
								<td colSpan={6} style={{ textAlign: 'right', fontWeight: 700 }}>
									Total
								</td>
								<td style={{ textAlign: 'right', fontWeight: 800, fontSize: 16 }}>
									{money(po?.amount_total)}
								</td>
							</tr>
						</tfoot>
					</table>
				</div>
			</div>

			{/* Payment & remarks */}
			<div className='vp-card vp-card-pad vp-mt-16'>
				<h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Payment &amp; Notes</h3>
				<div className='vp-grid-2'>
					<div className='vp-field vp-mb-0'>
						<label className='vp-label'>Payment Terms</label>
						<div>{po?.paymentTerm?.name || '—'}</div>
					</div>
					<div className='vp-field vp-mb-0'>
						<label className='vp-label'>Remarks</label>
						<div
							// Remarks may contain sanitised HTML from the buyer.
							// eslint-disable-next-line react/no-danger
							dangerouslySetInnerHTML={{ __html: po?.remarks || '—' }}
						/>
					</div>
				</div>
			</div>

			{/* Reference documents */}
			{documents.length > 0 && (
				<div className='vp-card vp-card-pad vp-mt-16'>
					<h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Reference Attachments</h3>
					<div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
						{documents.map((d: any, idx: number) => {
							const name = String(d?.doc || '').split('/').pop() || 'attachment';
							const isPdf = name.toLowerCase().endsWith('.pdf');
							return (
								<a
									key={d?._id || idx}
									href={fileUrl(d?.doc)}
									target='_blank'
									rel='noreferrer'
									style={{
										width: 120,
										textAlign: 'center',
										textDecoration: 'none',
									}}
									title={name}>
									{isPdf ? (
										<div
											style={{
												height: 90,
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												border: '1px solid var(--vp-border, #e2e8f0)',
												borderRadius: 8,
											}}>
											<VpIcon name='document' size={40} />
										</div>
									) : (
										<img
											src={fileUrl(d?.doc)}
											alt={name}
											style={{
												width: '100%',
												height: 90,
												objectFit: 'cover',
												borderRadius: 8,
												border: '1px solid var(--vp-border, #e2e8f0)',
											}}
										/>
									)}
									<div
										className='vp-muted'
										style={{
											fontSize: 12,
											marginTop: 6,
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap',
										}}>
										{name}
									</div>
								</a>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
};

export default VendorPurchaseOrderDetail;
